import { TextField } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { OrgChartHr } from 'routes/HumanResourcesModule/PayState/helpers/types';
import {
    MantyStatus,
    RecurrenceEnum,
    RetirementSystem,
} from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import AgentArrivalSelector from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/AddHypotheses/components/AgentArrivalSelector';
import AgentETPSelector from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/AddHypotheses/components/AgentETPSelector';
import AgentOrgChartHrSelector from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/AddHypotheses/components/AgentOrgChartHrSelector';
import {
    HrSimulation,
    SimulationDateString,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import Flex from 'ui/Flex/Flex';
import DateInput from 'components/DateInput';
import { useEnvelopeMantyStatusHypothesesChangesContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/contexts/EnvelopeMantyStatusHypothesesChangesContext';
import { FilteredMantyStatus } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/helpers/constants';

export type FormState = {
    agentETP: number;
    agentFirstName: string;
    agentLastName: string;
    echelon: string;
    filiere: string;
    ifse: number;
    isFormValid: boolean;
    jobLabel: string | null;
    jobNumber: string | null;
    majoredSalary: number;
    mantyStatus: MantyStatus;
    recurrence: RecurrenceEnum;
    officialGrade: string;
    retirementSystem: RetirementSystem;
    selectedOrgChartHrSoftwareId: string;
    selectedStartDate: SimulationDateString;
    selectedEndDate?: SimulationDateString;
};

type Props = {
    disableOrgChartSelector?: boolean;
    orgChartHrs: OrgChartHr[];
    formState: FormState;
    hrSimulation: HrSimulation;
    setFormState: React.Dispatch<React.SetStateAction<FormState>>;
};

const NonRecurrentStatus: string[] = [
    MantyStatus.SEASONAL_CONTRACTOR,
    MantyStatus.ACTIVITY_INCREASE_CONTRACTOR,
    MantyStatus.VACATAIRE,
    MantyStatus.ASSISTANTE_MATERNELLE,
    MantyStatus.EMPLOI_AIDE,
    MantyStatus.ELECTED_OFFICIAL,
    MantyStatus.OTHER,
];

const AgentArrivalForm = ({
    formState,
    setFormState,
    hrSimulation,
    orgChartHrs,
    disableOrgChartSelector,
}: Props) => {
    const { t } = useTranslation();
    const { nonRecurrentEnvelopeStatuses } =
        useEnvelopeMantyStatusHypothesesChangesContext();

    return (
        <>
            <Flex
                flexDirection="column"
                sx={{
                    marginTop: '1em',
                    gap: '2em',
                }}
            >
                <TextField
                    label={t(
                        'humanResources.simulation.addAgent.agentFirstName',
                    )}
                    value={formState.agentFirstName}
                    onChange={(event) =>
                        setFormState({
                            ...formState,
                            agentFirstName: event.target.value,
                        })
                    }
                    type="string"
                />
                <TextField
                    label={t(
                        'humanResources.simulation.addAgent.agentLastName',
                    )}
                    value={formState.agentLastName}
                    onChange={(event) =>
                        setFormState({
                            ...formState,
                            agentLastName: event.target.value,
                        })
                    }
                    type="string"
                />
                <AgentOrgChartHrSelector
                    orgChartHrs={orgChartHrs}
                    formState={formState}
                    setFormState={setFormState}
                    disabled={disableOrgChartSelector}
                />

                <TextField
                    label={t('humanResources.simulation.addAgent.jobLabel')}
                    value={formState.jobLabel}
                    onChange={(event) =>
                        setFormState({
                            ...formState,
                            jobLabel: event.target.value,
                        })
                    }
                    type="string"
                />
                <TextField
                    label={t('humanResources.simulation.addAgent.jobNumber')}
                    value={formState.jobNumber}
                    onChange={(event) =>
                        setFormState({
                            ...formState,
                            jobNumber: event.target.value,
                        })
                    }
                    type="string"
                />
                <AgentETPSelector
                    formState={formState}
                    setFormState={setFormState}
                />
                <DateInput
                    required
                    value={formState.selectedStartDate}
                    onChange={(date) =>
                        setFormState({
                            ...formState,
                            selectedStartDate: date as SimulationDateString,
                        })
                    }
                    labelOutside
                    label={t('humanResources.simulation.addAgent.startDate')}
                />
                <DateInput
                    value={formState.selectedEndDate}
                    onChange={(date) =>
                        setFormState({
                            ...formState,
                            selectedEndDate: date
                                ? (date as SimulationDateString)
                                : undefined,
                        })
                    }
                    allowEmpty
                    labelOutside
                    label={t('humanResources.simulation.addAgent.endDate')}
                    inputProps={{
                        min: formState.selectedStartDate,
                        max: hrSimulation.endDate,
                    }}
                />

                <AgentArrivalSelector
                    value={formState.mantyStatus}
                    formState={formState}
                    setFormState={setFormState}
                    label={t('humanResources.simulation.addAgent.mantyStatus')}
                    valueType="mantyStatus"
                    dataList={Object.values(MantyStatus).filter((item) => {
                        const isNonRecurrent =
                            formState.recurrence ===
                            RecurrenceEnum.NON_RECURRENT;
                        return isNonRecurrent
                            ? !nonRecurrentEnvelopeStatuses.includes(item) &&
                                  FilteredMantyStatus.includes(item)
                            : !NonRecurrentStatus.includes(item);
                    })}
                />
            </Flex>
        </>
    );
};

export default AgentArrivalForm;
